import { ExtendedGetResult } from '@fingerprintjs/fingerprintjs-pro';
import _get from 'lodash/get';
import mixpanel, { Response } from 'mixpanel-browser';
import queryString from 'qs';
import { VisitorTracking } from '../types/AnalyticsTracking';
import BrandclubStoreConfigUtils from '../utils/brandclubStoreConfigUtils';
import { AnalyticsTrackingEvent } from './events';
import * as ExternalTracking from './externalTracking';

type trackingEventType = AnalyticsTrackingEvent | `tombstone: ${string}`;

export const deferredPromise = <T = unknown>() => {
  let resolve: (value: T) => void = () => void 0;
  let reject: (reason?: Error | unknown) => void = () => void 0;
  let isDone = false;
  const p = new Promise<T>((r, e) => {
    resolve = (v) => {
      isDone = true;
      r(v);
    };
    reject = (err) => {
      isDone = true;
      e(err);
    };
  });
  return { p, resolve, reject, isDone: () => isDone } as DeferredPromise<T>;
};

export type DeferredPromise<T> = {
  p: Promise<T>;
  isDone: () => boolean;
  resolve: (value: T) => void;
  reject: (reason?: Error | unknown) => void;
};

let isInitialized = false;

const __PROD__ = BrandclubStoreConfigUtils.getAppStage() === 'prod';

const DEBUG = !__PROD__ && false;

if (DEBUG) {
  ExternalTracking.enableDebug();
}
const currentVisitorTracking: DeferredPromise<
  | {
      uniqueVisitorId: string | null;
      referrer: string | null;
      fingerprintResult?: ExtendedGetResult;
    }
  | undefined
> = deferredPromise();

export function setCurrentVisitorTracking(visitorTracking?: VisitorTracking) {
  if (visitorTracking && visitorTracking.uniqueVisitorId) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[DEBUG] resolving ', visitorTracking);
    }
    currentVisitorTracking.resolve(visitorTracking);
  }
}

export async function track(
  eventName: AnalyticsTrackingEvent | `tombstone: ${string}`,
  eventProps: { [key: string]: any },
  options?: {
    sendImmediately?: boolean;
  },
): Promise<
  | {
      eventName: string;
      eventParams: Record<string, any>;
    }
  | undefined
> {
  // Sends event to mixpanel
  const { location } = window;
  const params = queryString.parse(location.search, {
    ignoreQueryPrefix: true,
    arrayLimit: 100,
  });
  const pathName = _get(location.pathname.split('/'), '1') as unknown as string;
  const eventNameForService = `${eventName}`;
  const { brandPixels, ...rest } = eventProps || {};
  const paramsForService: Record<string, any> = {
    ...rest,
    ...params,
    pathName: pathName?.toLowerCase(),
    referrer: document.referrer,
    retailerId: rest?.retailerId || params.rid,
  };

  try {
    await ExternalTracking.track(eventNameForService, paramsForService, brandPixels);
  } catch (e) {
    console.error(`Error sending event (${eventNameForService}) to external tracking: ${(e as any).message}`);
  }

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log(`[DEBUG] sending '${eventNameForService}' with params: `, paramsForService);
  }

  try {
    if (!isInitialized) {
      initializeMixpanel();
    }
    if (isInitialized) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.log(`[DEBUG] Sending ${eventNameForService} to Mixpanel`);
      }
      const visitorTracking = await currentVisitorTracking.p;
      return new Promise((resolve, reject) => {
        try {
          if (visitorTracking && visitorTracking.fingerprintResult) {
            mixpanel.track(
              eventNameForService,
              {
                ...paramsForService,
                ipAddress: visitorTracking.fingerprintResult.ip,
                timeZone: visitorTracking.fingerprintResult.ipLocation?.timezone,
                location: {
                  city: visitorTracking.fingerprintResult.ipLocation?.city, // The city where the endpoint is located.
                  country: visitorTracking.fingerprintResult.ipLocation?.country?.code, // The two-letter code for the country or region of the endpoint. Specified as an ISO 3166-1 alpha-2 code, such as "US" for the United States.
                  latitude: visitorTracking.fingerprintResult.ipLocation?.latitude, // The latitude of the endpoint location, rounded to one decimal place.
                  longitude: visitorTracking.fingerprintResult.ipLocation?.longitude, // The longitude of the endpoint location, rounded to one decimal place.
                  postalCode: visitorTracking.fingerprintResult.ipLocation?.postalCode, // The postal code or zip code of the endpoint.
                },
              },
              {
                send_immediately: options?.sendImmediately,
              },
              (response: Response) => {
                if (response === 0 || (response as any)?.status === 0) {
                  reject(new Error('Mixpanel event not sent'));
                } else {
                  resolve({ eventName: eventNameForService, eventParams: paramsForService });
                }
              },
            );
          } else {
            mixpanel.track(
              eventNameForService,
              paramsForService,
              {
                send_immediately: options?.sendImmediately,
              },
              (response: Response) => {
                if (response === 0 || (response as any)?.status === 0) {
                  reject(new Error('Mixpanel event not sent'));
                } else {
                  resolve({ eventName: eventNameForService, eventParams: paramsForService });
                }
              },
            );
          }
        } catch (e) {
          reject(e);
        }
      });
    } else {
      console.error('mixpanel not initialized');
    }
  } catch (e) {
    console.error(`Error sending event (${eventNameForService}) to Mixpanel: ${(e as any).message}`);
  }
}

/**
 *  Logs to mixpanel if in prod, but warns to the console if not.
 *  @param msg a description of the event to be logged
 *  @param obj an optional object of related data to log with the message
 *  @param eventName an optional name for the event, defaults to 'warning'
 * */
export function warn(
  msg: string,
  obj?: { [key: string]: any },
  eventName: trackingEventType = AnalyticsTrackingEvent.WARNING,
) {
  if (__PROD__) {
    track(eventName, obj || {});
  } else if (obj) {
    console.warn(msg, obj);
  } else {
    console.warn(msg);
  }
}
/**
 *  Logs to mixpanel if in prod, but errors in the console if not.
 *  @param msg a description of the event to be logged
 *  @param obj an optional object of related data to log with the message
 *  @param eventName an optional name for the Mixpanel event, defaults to 'error'
 * */
export function error(
  msg: string,
  obj?: { [key: string]: any },
  eventName: trackingEventType = AnalyticsTrackingEvent.ERROR,
) {
  if (__PROD__) {
    track(eventName, obj ? { ...obj, message: msg } : { message: msg });
  } else if (obj) {
    console.error(msg, obj);
  } else {
    console.error(msg);
  }
}

/**
 * This function should be called when the application encounters an untenable state and cannot continue.  It will throw
 * an `Error` with the provided message and log the provided metadata object to the console if provided.  If in production,
 * it will trigger a Mixpanel event for the error and supply the provided metadata.
 *
 * @param msg a description of the error or unhandleable event that occurred
 * @param obj an optional object of related data to log with the message
 * @param eventName an optional name for the Mixpanel event, defaults to 'return panic'
 */
export function panic(
  msg: string,
  obj?: { [key: string]: any },
  eventName: trackingEventType = AnalyticsTrackingEvent.RETURN_PANIC,
): never {
  if (__PROD__) {
    track(eventName, obj ? { ...obj, msg } : { msg });
  } else if (obj) {
    console.error(msg, obj);
  }

  throw new Error(msg);
}

const getMixpanelApiHost = () => {
  const nodeEnv = import.meta.env?.MODE || process.env.NODE_ENV;
  if (nodeEnv === 'development') {
    return undefined; // will default to normal mixpanel host
  }

  return window.location.origin;
};

export function initializeMixpanel(domainOverride?: string) {
  if (__PROD__) {
    mixpanel.init('5f5add70c60a83f55ad7ba6d75faf5db', {
      ignore_dnt: true,
      api_host: domainOverride || getMixpanelApiHost(),
    });
  } else {
    mixpanel.init('cde1743169537cd47bc24fe7f0ee14b0', {
      ignore_dnt: true,
      api_host: domainOverride || getMixpanelApiHost(),
    });
  }
  isInitialized = true;
}

export function isMixpanelInitialized() {
  return isInitialized;
}

export function setMixpanelUserIdentity(
  visitorTracking?: VisitorTracking,
  user?: {
    given_name: string;
    family_name: string;
    email: string;
    userId: string;
  },
) {
  if (user) {
    const { given_name: firstName, family_name: lastName, email, userId } = user;
    mixpanel.identify(userId);
    mixpanel.register({
      FirstName: firstName,
      LastName: lastName,
      Email: email,
      UniqueVisitorId: visitorTracking?.uniqueVisitorId,
    });

    mixpanel.people.set({
      $first_name: firstName,
      $last_name: lastName,
      $email: email,
    });
  } else if (visitorTracking?.uniqueVisitorId) {
    mixpanel.identify(visitorTracking?.uniqueVisitorId);
  }
}

/**
 * Used to mark potentially dead code for future deletion. When code could potentially be dead, but you aren't sure,
 * put a tombstone in it and check the logs if it was ever hit. This helps make dead code deletion safer.
 * Idea taken from https://www.bugsnag.com/blog/javascript-refactoring-with-bugsnag-and-tombstones
 *
 * @param msg a custom message for mixpanel, should include the file and line (e.g. operations.ts:113)
 * @param obj an optional object to log with the message.
 */
export function tombstone(msg: string, obj: any = {}) {
  track(`tombstone: ${msg}`, obj);
}

export function trackRouteChange(previousLocation: Location, nextLocation: Location) {
  const params = queryString.parse(nextLocation.search, {
    ignoreQueryPrefix: true,
    arrayLimit: 100,
  });

  track(AnalyticsTrackingEvent.PAGE_VIEW, {
    ...params,
    currentUrl: nextLocation.pathname,
    previousUrl: previousLocation.pathname,
  });
}

export const trackError = (url: string, err: Error) => {
  track(AnalyticsTrackingEvent.ERROR_PAGE, {
    url,
    message: err.message,
    backtrace: err.stack || '',
    lineNumber: (err as any).lineNumber,
  });
};
